// src/pages/Checkout/Checkout.jsx
// FRONT OFFICE - Fixed Checkout page with persistent WhatsApp payment option

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaWhatsapp, FaSpinner } from 'react-icons/fa';
import { useCart } from '../../contexts/CartContext';
import { useOrders } from '../../contexts/OrderContext';
import { useAuth } from '../../contexts/AuthContext';
import './Checkout.css';

const Checkout = () => {
  const { cartItems, cartTotals, emptyCart, loading: cartLoading ,cart} = useCart();
  const { placeOrder, loading: orderLoading } = useOrders();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [customerInfo, setCustomerInfo] = useState({
    name: currentUser?.name || '',
    phone: currentUser?.phone || '',
    address: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [orderTotal, setOrderTotal] = useState(0);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate('/login', { 
        state: { 
          from: '/checkout',
          message: 'Please log in to checkout' 
        } 
      });
      return;
    }
    
    // Redirect to cart if cart is empty and no order has been placed
    if (cartItems.length === 0 && !cartLoading && !orderPlaced) {
      navigate('/cart');
    }

    // Update customer info when currentUser changes
    if (currentUser) {
      setCustomerInfo(prev => ({
        ...prev,
        name: currentUser.name || prev.name,
        phone: currentUser.phone || prev.phone
      }));
    }
  }, [currentUser, cartItems, cartLoading, orderPlaced, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!customerInfo.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!customerInfo.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    
    if (!customerInfo.address.trim()) {
      errors.address = 'Address is required';
    }
    
    return errors;
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      // Display the first error
      setError(Object.values(validationErrors)[0]);
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Create order in Firebase
      const order = await placeOrder(cart.id, {
        customerName: customerInfo.name,
        customerPhone: customerInfo.phone,
        shippingAddress: customerInfo.address,
        paymentMethod: 'WhatsApp',
        paymentStatus: 'pending',
        shippingCost: cartTotals.shippingCost,
        subtotal: cartTotals.subtotal,
        total: cartTotals.total
      });
      
      setOrderId(order.id);
      setOrderTotal(order.total);
      setOrderPlaced(true);
      
      // Clear cart after successful order
      await emptyCart();
      
    } catch (err) {
      console.error('Error placing order:', err);
      setError('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppRedirect = () => {
    // Format cart items for message if available
    let itemsList = 'Order placed successfully';
    
    // Create WhatsApp message
    const message = `*New Order Request*%0A%0A` +
      `*Order ID:* ${orderId}%0A` +
      `*Customer Name:* ${customerInfo.name}%0A` +
      `*Phone:* ${customerInfo.phone}%0A` +
      `*Address:* ${customerInfo.address}%0A%0A` +
      `*Total Amount:* €${orderTotal.toFixed(2)}%0A%0A` +
      `Please confirm this order.`;
    
    // WhatsApp number from the data
    const whatsappNumber = '+491632055734';
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');
    
    // Navigate to confirmation page
    navigate('/order-confirmation', { 
      state: { 
        orderId: orderId,
        orderTotal: orderTotal
      } 
    });
  };

  // If order is placed, show WhatsApp redirect button
  if (orderPlaced) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="order-success">
            <div className="success-icon">✓</div>
            <h1>Order Placed Successfully!</h1>
            <p>Your order {orderId.substring(0, 8)} has been placed successfully.</p>
            <p>To complete your order, please finalize payment via WhatsApp. Click the button below to initiate the payment process.</p>
            
            <button className="whatsapp-btn" onClick={handleWhatsAppRedirect}>
              <FaWhatsapp className="whatsapp-icon" />
              Complete Payment via WhatsApp
            </button>

            <div className="order-success-actions">
              <Link to={`/order-confirmation?id=${orderId}`} className="btn btn-primary">
                View Order Details
              </Link>
              <Link to="/shop" className="btn btn-outline">
                Continue Shopping
              </Link>
            </div>

            <p className="payment-note">
              <strong>Note:</strong> If you need to complete the payment later, you can always find this order in your <Link to="/my-account">account dashboard</Link>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (cartLoading || loading) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="checkout-loading">
            <FaSpinner className="spinner" />
            <p>Processing your checkout...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <h1>Complete Your Order</h1>
        
        {error && <div className="checkout-error">{error}</div>}
        
        <div className="checkout-form">
          <h2>Customer Information</h2>
          <form onSubmit={handlePlaceOrder}>
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                name="name"
                value={customerInfo.name}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label>WhatsApp Number</label>
              <input
                type="tel"
                name="phone"
                value={customerInfo.phone}
                onChange={handleChange}
                required
                placeholder="Include country code, e.g., +49..."
              />
              <small>We'll send payment instructions to this WhatsApp number</small>
            </div>
            
            <div className="form-group">
              <label>Delivery Address</label>
              <textarea
                name="address"
                value={customerInfo.address}
                onChange={handleChange}
                required
                rows="3"
                placeholder="Enter your full shipping address"
              />
            </div>
            
            <div className="order-summary">
              <h3>Your Order</h3>
              {cartItems.map(item => (
                <div key={item.id} className="order-item">
                  <span>{item.product.name} × {item.quantity}</span>
                  <span>€{(item.product.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              
              <div className="order-subtotal">
                <span>Subtotal:</span>
                <span>€{cartTotals.subtotal.toFixed(2)}</span>
              </div>
              
              <div className="order-shipping">
                <span>Shipping:</span>
                <span>{cartTotals.shippingCost === 0 ? 'FREE' : `€${cartTotals.shippingCost.toFixed(2)}`}</span>
              </div>
              
              <div className="order-total">
                <span>Total:</span>
                <span>€{cartTotals.total.toFixed(2)}</span>
              </div>
            </div>
            
            <div className="payment-info">
              <p>
                <strong>Payment Method: WhatsApp</strong>
              </p>
              <p>
                After placing your order, you will be redirected to WhatsApp to complete your payment.
                Please have your payment information ready.
              </p>
              <div className="whatsapp-info">
                <FaWhatsapp className="whatsapp-icon" />
                <p>Payment will be made securely through WhatsApp conversation with our customer service</p>
              </div>
            </div>
            
            <button 
              type="submit" 
              className="place-order-btn"
              disabled={loading || orderLoading}
            >
              {loading || orderLoading ? (
                <>Processing... <FaSpinner className="spinner-small" /></>
              ) : (
                <>Place Order</>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Checkout;